import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import ReactGA from "react-ga4";
import { animated } from "react-spring";
import { SyncOutlined } from "src/icons";
import styles from "./index.module.scss";
import AllOrg from "../AllOrg";
import PinnedContainer from "src/shared/components/PinnedContainer";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import { fetchHome, FetchHomePayload, searchPin, SearchPinPayload, searchReset } from "src/store/home";
import { fetchAllOrgMetrics, FetchAllOrgMetricsPayload } from "src/store/allOrg";
import { categoryReset, setCategoriesFilters } from "src/store/category";
import { clearDetails, setFilters } from "src/store/details";
import {
  changeTimeTabAppWide,
  setComingBackFromDetailsPage,
  setIsRoleCalloutShownAction,
  UserRoles,
} from "src/store/app";
import { logoutAction } from "src/store/login";
import RibbonSlider from "src/shared/components/RibbonSlider";
import { useRedux } from "src/shared/hooks";
import ManageMetrics from "./ManageMetric";
import Search from "src/shared/components/Search";
import { IconClass, IRootState, SearchTarget } from "src/shared/types";
import { IActionFactory, IReduxOperations } from "src/store";
import useDragger, { DRAGPANEL_TOP_THRESHOLD } from "./useDragger/index2";
import styled from "@emotion/styled";
import userService from "src/shared/services/user.service";
import colors from "src/styles/variables.scss";
import { checkIfIOSDevice, noop, safeGet, useEffectOnPropsChange } from "src/shared/utils";
import RoleCallout from "./RoleCallout";
import Permissions from "../AllOrg/Permissions";
import { RunningAnobisMode } from "src/shared/config";
import { getServiceName, ServiceName } from "src/utils/getservicename";

const CALL_DELAY = 1500;

interface IState {
  metrics: IReduxOperations;
  search: IReduxOperations;
  selectedTab: any;
  userRoles: IReduxOperations<UserRoles>;
  isRoleCalloutShown: boolean;
}

type MappedActionProps = {
  fetchHome(payload: FetchHomePayload);
  searchPin(payload: SearchPinPayload);
  setCategoriesFilters: IActionFactory<symbol, IObject<any>>;
  categoryReset: IActionFactory<symbol, unknown>;
  searchReset: IActionFactory<symbol, unknown>;
  logoutAction();
  setFilters: IActionFactory<symbol, IObject<any>>;
  clearDetails: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setIsRoleCalloutShown: IActionFactory<symbol, boolean>;
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const SliderContainer = styled.article`
  position: absolute;
  z-index: 1;
  width: inherit;
  height: 100vh;
  background: ${colors.blackBackground};
`;

const DragPanel = styled.section<{ translateYVal?: number }>`
  /* transform: translate3d(
    0,
    ${({ translateYVal }) => (translateYVal ? translateYVal : 0)}px,
    0
  ); */
  /* will-change: transform; */
  transition: ${checkIfIOSDevice() ? "none" : "transform 0.2s cubic-bezier(0.175, 0.985, 0.32, 1.275)"};
  /* -webkit-transition: none; */
  /* Disabling transition on Firefox due to jitter */
  -moz-transition: none;
  /* backface-visibility: hidden; */
  /* > * {
    pointer-events: none;
  } */
`;

const SyncIcon = styled(SyncOutlined)`
  font-size: 1.5rem;
  height: min-content;
  width: 1.5rem;
  line-height: 15px;
`;

const ROLE_CALLOUT_SHOWN = "ROLE_CALLOUT_SHOWN";

const MainContainer: React.FC<RouteComponentProps> = ({ history }) => {
  useEffect(() => {
    // ReactGA.pageview("/home", [], "Home");
    ReactGA.send({ hitType: "pageview", page: "/home", title: "Home" });
    if (getServiceName() === ServiceName.ANOBIS_IMCOM) {
      ReactGA.event({
        category: "Anobis IMCom",
        label: "Anobis IMCom home page",
        action: "Redirect to IMCom",
      });
      history.push("/im-com-app");
    }
    // Force redirect to India2MIS Home page
    if (getServiceName() === ServiceName.ANOBIS_INDIA_2) {
      ReactGA.event({
        category: "Anobis India2",
        label: "Anobis India2 home page",
        action: "Redirect to India2",
      });
      history.push("/india-2-app");
    }
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const [manageMetricsFlag, setManageMetricsFlag] = useState(false);
  const dragPanel = useRef<HTMLDivElement>(null as any);
  const allOrgElement = React.useRef<HTMLDivElement>(null as any);
  const [page, setPage] = useState(1);
  const [targetSearch, setTargetSearch] = useState(SearchTarget.HOME);
  const [showSearch, toggleSearch] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null as any);

  const showDetails = useCallback(
    (id, name) => {
      mappedProps.setComingBackFromDetailsPage(true);

      history.push(`/details/pin/${id}`);
      ReactGA.event({
        category: "Pin Clicked on L1a",
        label: `Pin: ${name} on L1a`,
        action: "Click from L1a",
      });
    },
    [history]
  );

  // const windowHeight = useMemo(() => window.innerHeight, [window.innerHeight]);
  const [showRibbon, setShowRibbon] = useState(false);

  useDragger({
    dragPanel,
    onPanStart(e: HammerInput, isDockedUp: boolean) {
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "none";
        }
      }
    },
    onPanEnd(e: HammerInput, isDockedUp: boolean) {
      setShowRibbon(isDockedUp);
      if (isDockedUp) {
        document.body.style.overflowY = "hidden";
      } else {
        document.body.style.overflowY = "auto";
      }
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "initial";
        }
      }
    },
    shouldPerformTouchGesture({ target: touchedTargetEl, direction }: HammerInput) {
      const sensitiveContainerEl = allOrgElement.current;
      if (sensitiveContainerEl) {
        const isTragetInsideSensitiveZone = sensitiveContainerEl.contains(touchedTargetEl);
        return !isTragetInsideSensitiveZone;
        // FIXME: Find a way to drag down from inside the sensitive zone
        // if (isTragetInsideSensitiveZone) {
        //   /* Check for the case when the user is swiping/panning downwards and the scrollable
        //    element is on top already  */
        //   return (
        //     direction === Hammer.DIRECTION_DOWN &&
        //     sensitiveContainerEl.scrollTop === 0
        //   );
        // }
      }
      return true;
    },
  });

  const {
    setFilters: setL3Fiters,
    clearDetails: clearL3Data,
    ...mappedProps
  } = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      metrics: state.home.pinnedMetrics.metrics,
      search: state.home.pinnedMetrics.search,
      loadMorePending: state.home.pinnedMetrics.loadMorePending,
      chartsDataPending: state.home.pinnedMetrics.chartsDataPending,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      userRoles: state.app.userRoles,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    {
      fetchHome,
      searchPin,
      searchReset,
      logoutAction,
      setCategoriesFilters,
      categoryReset,
      setFilters,
      clearDetails,
      changeTimeTabAppWide,
      setIsRoleCalloutShown: setIsRoleCalloutShownAction,
      fetchAllOrgMetrics,
      setComingBackFromDetailsPage,
    }
  );

  const dontShowRoleCallout = useCallback(() => {
    localStorage.setItem(ROLE_CALLOUT_SHOWN, "true");
    mappedProps.setIsRoleCalloutShown(true);
  }, []);

  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    mappedProps.logoutAction();
  }, []);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [shouldShowPermissions, setShouldShowPermissions] = useState(false);

  const onRefresh = useCallback(() => {
    if (isRefreshClicked) return;
    setIsRefreshClicked(Date.now());
    setTimeout(() => {
      setPage(1);
      mappedProps.fetchHome({ pageNo: 1, fresh: true });
      mappedProps.fetchAllOrgMetrics({ fresh: true });
    }, CALL_DELAY);
  }, [isRefreshClicked]);

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending") === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending"), isRefreshClicked]);

  // as states are shared between Anobis and IMCoM app. Force timeTab update, if anyone switches between 2, to avoid any issues.
  useEffect(() => {
    if (RunningAnobisMode.SHOW_IM_COM_TIME_TAB.includes(mappedProps.selectedTab)) {
      mappedProps.changeTimeTabAppWide("Today");
    }
  });

  const onSearchClick = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(true);
  }, []);

  const closeSearch = useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = undefined;
    mappedProps.searchReset();
    toggleSearch(false);
    setTargetSearch(SearchTarget.HOME);
  }, []);

  const handleChangeTab = useCallback(
    ({ label }: { label: string }) => {
      setPage(1);
      mappedProps.changeTimeTabAppWide(label);
      // mappedProps.fetchHome(1);
    },
    [mappedProps]
  );
  useEffectOnPropsChange(() => {
    mappedProps.fetchHome({ pageNo: 1 });
  }, [mappedProps.selectedTab]);

  const loadMore = useCallback(() => {
    if (
      !(safeGet(mappedProps, "metrics.pending", false) || safeGet(mappedProps, "loadMorePending", false)) &&
      safeGet(mappedProps, "metrics.payload.hasMore", false)
    ) {
      mappedProps.fetchHome({ pageNo: page + 1 });
      setPage(page + 1);
    }
  }, [mappedProps.metrics, safeGet(mappedProps, "loadMorePending"), page]);

  useEffect(() => {
    // action to load page data
    // if user is coming back from detail page store previous state, no need to fetch again
    if (safeGet(mappedProps, "comingBackFromDetailsPage") !== true) {
      mappedProps.fetchHome({ pageNo: 1, fresh: true });
      mappedProps.fetchAllOrgMetrics({ fresh: true });
      mappedProps.setCategoriesFilters({});
      mappedProps.categoryReset();
      setL3Fiters({});
      clearL3Data();
    } else {
      mappedProps.setComingBackFromDetailsPage(false);
      // If user is coming back from details page, fetch data only if it was not fetched before successfully
      if (safeGet(mappedProps, "metrics.success") === false) {
        mappedProps.fetchHome({ pageNo: 1, fresh: true });
        mappedProps.fetchAllOrgMetrics({ fresh: true });
      }
    }
  }, []);

  const scrollHandler = useCallback(() => {
    const currentDiv = containerRef.current;
    if (currentDiv.scrollTop > currentDiv.scrollHeight - currentDiv.offsetHeight - 180) {
      loadMore();
    }
  }, [loadMore]);

  // setting scroll position on page load or going back from details page
  useEffect(() => {
    const currentDiv = containerRef.current;
    currentDiv?.addEventListener("scroll", () => {
      localStorage.setItem("AnobisYscrollPositionPinnedPage", currentDiv?.scrollTop.toString());
    });
    if (mappedProps.metrics.success) {
      const scrollPosition: any = localStorage.getItem("AnobisYscrollPositionPinnedPage");
      currentDiv?.scrollTo({
        top: parseInt(scrollPosition),
        behavior: "smooth",
      });
    }
  });

  const options = useMemo(
    () => [
      {
        className: "",
        iconClass: IconClass.SEARCH,
        handleClick: onSearchClick,
      },
      {
        className: "",
        iconClass: IconClass.COMPARE,
        handleClick: () => history.push("/metric-comparison"),
      },
    ],
    [onSearchClick]
  );

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const searchMetric = useCallback((text: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = undefined;
    if (!text || !text.trim()) {
      mappedProps.searchReset();
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      mappedProps.searchPin({ text: text.trim(), targetSearch });
    }, 300);
  }, [targetSearch]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  return (
    <>
      {!manageMetricsFlag && (
        <animated.section
          className={`${styles["container"]} ${showRibbon ? styles.overlayOpen : ""} ${
            showSearch ? styles["display-none"] : ""
          }`}
          style={{ position: "absolute" }}
          ref={(target) => {
            containerRef.current = target as HTMLDivElement;
          }}
          onScroll={scrollHandler}
        >
          {showRibbon && safeGet(mappedProps, "metrics.success", false) ? (
            <SliderContainer>
              <RibbonSlider height={DRAGPANEL_TOP_THRESHOLD} pins={safeGet(mappedProps, "metrics.payload", [])} />
            </SliderContainer>
          ) : null}
          <div className={styles["home-top-nav"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <TopNavbar
              handleClick={noop}
              headerText=""
              isHome
              onLogout={onLogout}
              onPermissionsShow={() => setShouldShowPermissions(true)}
            >
              <>
                {safeGet(mappedProps, "chartsDataPending", false) || !!isRefreshClicked ? (
                  <SyncIcon spin />
                ) : (
                  <SyncIcon onClick={onRefresh} />
                )}
                {options.map((option) => (
                  <MenuItem key={option.iconClass} {...option} animate={option.iconClass === IconClass.COMPARE} />
                ))}
              </>
            </TopNavbar>
          </div>

          <div className={styles["pinned-items"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <PinnedContainer
              isPulledUp={showRibbon}
              pins={mappedProps.metrics}
              showDetails={showDetails}
              isPending={safeGet(mappedProps, "metrics.pending", false)}
              onChangeTab={handleChangeTab}
              switchToManageMetrics={setManageMetricsFlag}
              selectedTab={mappedProps.selectedTab}
            />
          </div>

          <DragPanel
            className={styles["dragger"]}
            ref={dragPanel}
            // translateYVal={translateYVal}
          >
            <div className={styles["overlay-draggable-handle"]}>
              <div className={styles["dragable-handle-grip"]} />
            </div>
            <div id="overlayID" className={styles["overlay"]}>
              <AllOrg
                ref={allOrgElement}
                onSearchClick={onSearchClick}
                setTargetSearch={setTargetSearch}
                isPulledUp={showRibbon}
                showSearch={showSearch}
                onShowPermissions={() => setShouldShowPermissions(true)}
                handleRefresh={onRefresh}
              />
            </div>
          </DragPanel>
        </animated.section>
      )}
      {manageMetricsFlag && (
        <>
          <ManageMetrics refreshHome={onRefresh} switchBackToHome={setManageMetricsFlag} />
        </>
      )}
      {showSearch && (
        <Search
          closeSearch={closeSearch}
          searchMetric={searchMetric}
          searchResult={safeGet(mappedProps, "search.payload", []) || []}
          open={showSearch}
          target={targetSearch}
          searchSuccess={safeGet(mappedProps, "search.success", false)}
          searchPending={safeGet(mappedProps, "search.pending", false)}
          resetSearch={mappedProps.searchReset}
        />
      )}
      {mappedProps.userRoles.success && (
        <RoleCallout
          show={!mappedProps.isRoleCalloutShown && !mappedProps.userRoles.payload.hasFullAccess}
          onViewDetailsClick={() => setShouldShowPermissions(true)}
          onOK={dontShowRoleCallout}
        />
      )}
      {shouldShowPermissions && (
        <Permissions roles={mappedProps.userRoles} onClose={() => setShouldShowPermissions(false)} />
      )}
    </>
  );
};

export default MainContainer;
